/**
 * Блок «Цены, труд и доходы населения» (US-35.1 FR-5, FR-6; вторая волна —
 * US-35.2 FR-4…FR-6, DP-483) — группы выдачи `INFLATION`, `LABOR_INCOME`
 * и `PRODUCTIVITY`, каждая своими карточками.
 *
 * **Инфляция**: выдача второй волны несёт ДВЕ базы отдельными показателями
 * (`CPI` — «к соответствующему периоду прошлого года», `CPI_DEC` — «к
 * декабрю предыдущего года»), смешивать их составляющие запрещено (US-36.2
 * FR-3.3). Экран ПЕРЕКЛЮЧАЕТСЯ на базу «к декабрю», как только она
 * поставлена (US-35.2 FR-4.1): показывается `CPI_DEC`, прежняя карточка
 * убирается — изменение значений ожидаемо (FR-4.2). Пока база не
 * поставлена, остаётся прежняя. Подпись базы по-прежнему из ответа.
 *
 * **Рынок труда** (FR-5): вторая волна добавила зарплату в тенге и
 * среднедушевые доходы — карточки приходят из ответа и встают по `order`.
 *
 * **Рост производительности труда** (FR-6): показатели ручного ввода (доля
 * самозанятых к ЭАН, новые рабочие места) — не внесённое значение СКРЫТО,
 * а не показано нулём или «нет данных» (FR-6.3): пустая карточка ручного
 * показателя означает лишь, что оператор его не ввёл.
 *
 * Показатель, который источник на этом уровне не публикует (индекс
 * производительности труда на районе), карточки не получает (DP-522).
 *
 * Порядок групп и показателей внутри — из поля `order` ответа.
 */
import { useTranslation } from 'react-i18next';

import {
  EconomyIndicatorCard,
  isLevelNotPublished,
} from './EconomyIndicatorCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import type {
  EconomyIndicator,
  PricesLaborResponse,
} from '../../../types/analytics';

interface PricesLaborBlockProps {
  data: PricesLaborResponse | null;
  loading: boolean;
}

/** Показатель целиком из ручного ввода — скрыт, пока не внесён (FR-6.3). */
const isEmptyManual = (indicator: EconomyIndicator): boolean =>
  !indicator.has_data &&
  indicator.values.length > 0 &&
  indicator.values.every((value) => value.origin === 'MANUAL');

export const PricesLaborBlock = ({ data, loading }: PricesLaborBlockProps) => {
  const { t } = useTranslation();

  if (data !== null && (!data.widget_enabled || !data.level_supported)) {
    return null;
  }

  const all = (data?.groups ?? []).flatMap((group) => group.indicators);
  // FR-4.1: база ИПЦ переключается на «к декабрю» ВМЕСТО прежней, как
  // только поставлена; две карточки одного индекса путали бы читателя.
  const decDelivered = all.some((i) => i.key === 'CPI_DEC' && i.has_data);
  const indicators = all.filter((indicator) => {
    if (indicator.key === 'CPI') return !decDelivered;
    if (indicator.key === 'CPI_DEC') return decDelivered;
    return !isEmptyManual(indicator) && !isLevelNotPublished(indicator);
  });

  return (
    <section className="space-y-3.5" data-testid="block-economy-prices-labor">
      <div className="flex items-center gap-4 pt-2">
        <h3 className="shrink-0 text-lg font-extrabold">
          {t('analytics.economy.blocks.pricesLaborTitle')}
        </h3>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
      </div>

      {loading ? (
        <BlockLoader testId="economy-prices-labor-loader" />
      ) : indicators.length === 0 ? (
        <p
          className="text-sm lowercase text-text-muted"
          data-testid="block-placeholder"
        >
          {t('common.noData')}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-2">
          {indicators.map((indicator) => (
            <EconomyIndicatorCard
              key={indicator.key}
              indicator={indicator}
              testId={`economy-prices-labor-${indicator.key}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};
